// A9 — THE SAVED-ROW MERGER. Brief 65 (brief 50 §4), the eighth of the sixteen.
//
// What the sync engine does with a row the server just handed back. Two groups,
// five blocks:
//
//   src/App.tsx:4493-4511@8fdbbd6c x src/App.tsx:4555-4570@8fdbbd6c  80.2%  merge + tell
//   src/App.tsx:6153-6156@8fdbbd6c x src/App.tsx:7720-7723@8fdbbd6c  89.7%  append if absent
//   src/App.tsx:6153-6156@8fdbbd6c x src/App.tsx:7746-7749@8fdbbd6c  89.7%
//   src/App.tsx:7720-7723@8fdbbd6c x src/App.tsx:7746-7749@8fdbbd6c  89.7%
//
// THE 89.7% THREE DIFFER ONLY IN THE NAME OF THE SAVED ROW — `saved`, `savedCl`,
// `savedCp`. Nothing to parameterise but the value itself.
//
// THE 80.2% PAIR SHARES TWO THINGS AND NOT A THIRD, and the third is why only
// two moved. Both copies merge the saved fields into the row with that id, and
// both toast when the server auto-created subtasks. Only ONE of them also has
// an id-SWAP branch (a client temp id being replaced by a server id, which
// dedupes rather than merges), and only the other calls `_row("update", …)`.
// Those two stay where they are: they are not a difference between two copies
// of one thing, they are two things one copy has.

// A row the server handed back: it has an id, and everything else about it is
// the caller's business. Written as a type rather than `any` because these
// three seams are NEW — the ratchet's rule is that a new file adds none, and
// the `any`s in the originals were on inline arrow parameters that this move
// deletes rather than carries.
type Row = { id?: unknown; [k: string]: unknown };

export const mergeSavedRow = (arr: Row[], id: unknown, saved: Row): Row[] =>
  arr.map((x) => (x.id === id ? { ...x, ...saved } : x));

// 207B §5 — the same merge, for an UPDATE's answer, minus what it must not
// touch: a field the user changed AFTER the request left. The row as it stands
// now (`current`), what the request carried (`sent`), what the server answered
// (`saved`): a field still holding what was sent takes the server's value (it
// may have normalised it); a field changed since keeps what was typed — its own
// save is on its way (src/lib/saveQueue.mjs); a field the request did not carry
// (an `updatedAt`) takes the server's. `mergeSavedRow` over the list sync's
// update answers put an older answer back under the caret (§5's « 98765 » kept
// as 95).
const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);
export const mergeAnswer = (current: Row, sent: Row, saved: Row): Row => {
  const out: Row = { ...current };
  for (const [k, v] of Object.entries(saved)) {
    if (!(k in sent) || same(current[k], sent[k])) out[k] = v;
  }
  return out;
};
/** `mergeAnswer` on the row with that id — `mergeSavedRow`'s shape, for an update's answer. */
export const mergeAnswerInto = (arr: Row[], id: unknown, sent: Row, saved: Row): Row[] =>
  arr.map((x) => (x.id === id ? mergeAnswer(x, sent, saved) : x));

export const appendSavedRow = (prev: unknown, saved: Row): Row[] => {
  const p: Row[] = Array.isArray(prev) ? prev : [];
  return p.some((x) => x.id === saved.id) ? p : [...p, saved];
};

// 207 §1.3 — THE CREATE BRANCH'S ID-SWAP, extracted from the list sync
// (src/App.tsx, `persist`'s `_onCreated`) so a recording can drive the same
// code the browser runs. MOVED, NOT CHANGED: the temporary row is replaced by
// the server's, then every later row carrying the same id is dropped, FIRST
// WINS — Phase 29 (item 5)'s reason: the SSE hub can deliver our OWN create
// event before the POST response resolves (the route publishes before
// res.json), so the live-stream upsert may already have inserted the server
// row next to the temporary one; keep one. It is also exactly where 207 §1
// lost rows: a server id equal to the temporary id of a row STILL WAITING
// made that row the "later duplicate", and it was dropped. `tempId()`
// (src/lib/tempId) is what makes that impossible — this function did not
// change for it.
export const swapCreatedRow = (arr: Row[], tempRowId: unknown, saved: Row): Row[] => {
  const seen = new Set<unknown>();
  return arr
    .map((x) => (x.id === tempRowId ? saved : x))
    .filter((x) => {
      if (seen.has(x.id)) return false;
      seen.add(x.id);
      return true;
    });
};

// The count is interpolated into the fallback exactly as both copies wrote it,
// including the plural — a fallback is the string a firm sees when the key is
// missing, so it is behaviour and not decoration.
export function toastInjectedSubtasks(
  saved: Row | null | undefined,
  toast: (m: string) => void,
  t: (k: string, o?: Record<string, unknown>) => string,
) {
  if (saved && Number(saved.injectedSubtasks) > 0) {
    toast(
      t("toast.subtasks_injected", {
        count: saved.injectedSubtasks,
        fallback: `Auto-created ${saved.injectedSubtasks} subtask${saved.injectedSubtasks === 1 ? "" : "s"}`,
      }),
    );
  }
}

/* 212 §18 — THE STOP, SAID. An address written through a save that SharePoint
   could not be asked about (Microsoft 365 not connected, throttled, silent past
   its bound) is stored as typed, and the answer carries `folderUnchecked`; the
   words for saying so, once, beside the save — or null when there is nothing
   to say. A refused address never reaches here: its 400
   (`case.folder.not_found`) is `_apiErr`'s, which names the address. Returns
   the words rather than taking a toast: a callback parameter here would be one
   more named parameter in a function type, which the committed
   `no-unused-vars` counts. */
export function folderUncheckedWords(saved: unknown, translate: unknown): string | null {
  // `unknown` at both seams: the sync engine hands untyped rows and an untyped `t`, and an
  // `unknown` parameter takes them without an unsafe argument; narrowed here, once
  const t = translate as Parameters<typeof toastInjectedSubtasks>[2];
  const note: unknown = saved && typeof saved === "object" ? (saved as Row).folderUnchecked : null;
  if (!note || typeof note !== "object") return null;
  const { address, reason } = note as { address?: unknown; reason?: unknown };
  let why = t("folder_check.why_other", { fallback: "SharePoint did not answer" });
  if (reason === "m365_not_connected") why = t("folder_check.why_not_connected", { fallback: "Microsoft 365 is not connected" });
  else if (reason === "throttled") why = t("folder_check.why_throttled", { fallback: "SharePoint is busy" });
  const where = typeof address === "string" ? address : "";
  return t("toast.folder.unchecked", {
    address: where,
    why,
    fallback: "The folder « " + where + " » could not be checked in SharePoint (" + why + ") — the address is saved as typed.",
  });
}
